<template>
  <section class="bg-white overflow-hidden">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">

        <!-- ── Left Content ── -->
        <div class="order-2 lg:order-1">

          <!-- Tag line -->
          <div class="inline-flex items-center gap-2 bg-primary-50 text-primary-500 text-sm font-medium px-4 py-1.5 rounded-full mb-6">
            <span class="w-2 h-2 rounded-full bg-primary-500 animate-pulse"></span>
            Rwanda's #1 Property Platform
          </div>

          <!-- Headline -->
          <h2 class="text-2xl md:text-3xl lg:text-4xl font-bold text-neutral-800 leading-tight mb-6">
            Find Your Best <br />
            <span class="text-primary-500">Real Estate</span>
            in Rwanda
          </h2>

          <!-- Sub-text -->
          <p class="text-neutral-500 text-lg mb-8 max-w-md">
            Buy, sell, or rent properties across Rwanda with confidence. Trusted by thousands of families.
          </p>

          <!-- Buy / Sell Toggle -->
          <div class="flex items-center gap-1 bg-neutral-100 rounded-xl p-1 w-fit mb-6">
            <button
              @click="activeTab = 'buy'"
              :class="activeTab === 'buy' ? 'bg-white text-primary-500 shadow-sm' : 'text-neutral-500 hover:text-neutral-700'"
              class="px-6 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200"
            >
              Buy House
            </button>
            <button
              @click="activeTab = 'sell'"
              :class="activeTab === 'sell' ? 'bg-white text-primary-500 shadow-sm' : 'text-neutral-500 hover:text-neutral-700'"
              class="px-6 py-2.5 rounded-lg text-sm font-semibold transition-all duration-200"
            >
              Sell House
            </button>
          </div>

          <!-- Search Bar -->
          <div class="flex flex-col sm:flex-row gap-3 mb-8">
            <div class="relative flex-1">
              <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <input
                v-model="searchQuery"
                @keyup.enter="onSearchAction"
                type="text"
                :placeholder="activeTab === 'buy' ? 'Enter city or neighbourhood...' : 'Enter your property address...'"
                class="w-full pl-10 pr-4 py-3.5 border border-neutral-200 rounded-xl text-sm text-neutral-700 placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition"
              />
            </div>
            <button
              @click="onSearchAction"
              class="px-7 py-3.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold rounded-xl transition-colors shadow-sm whitespace-nowrap"
            >
              {{ activeTab === 'buy' ? 'Search' : 'List My Property' }}
            </button>
          </div>

          <!-- Star Rating Badge -->
          <div class="flex items-center gap-3">
            <!-- Reviewer avatars -->
            <div class="flex -space-x-2">
              <template v-if="reviewsLoading">
                <div v-for="i in 4" :key="i" class="w-8 h-8 rounded-full border-2 border-white bg-neutral-200 animate-pulse"></div>
              </template>
              <template v-else-if="reviewAvatars.length">
                <div
                  v-for="(av, i) in reviewAvatars" :key="i"
                  class="w-8 h-8 rounded-full border-2 border-white flex items-center justify-center text-xs font-bold text-white flex-shrink-0"
                  :style="{ background: av.gradient }"
                  :title="av.name"
                >{{ av.initials }}</div>
              </template>
            </div>
            <!-- Rating numbers -->
            <div class="flex items-center gap-1.5">
              <div class="flex text-yellow-400 text-sm">
                <span v-for="s in 5" :key="s" :class="s <= Math.round(avgRating) ? 'text-yellow-400' : 'text-neutral-300'">★</span>
              </div>
              <span class="text-sm font-semibold text-neutral-700">
                <template v-if="reviewsLoading"><span class="inline-block w-8 h-3.5 bg-neutral-200 animate-pulse rounded"></span></template>
                <template v-else>{{ avgRating.toFixed(1) }}/5</template>
              </span>
              <span class="text-sm text-neutral-400">
                — from
                <template v-if="reviewsLoading"><span class="inline-block w-8 h-3 bg-neutral-200 animate-pulse rounded"></span></template>
                <template v-else>{{ totalReviews.toLocaleString() }} reviews</template>
              </span>
            </div>
          </div>

        </div>

        <!-- ── Right Side — Property Showcase Carousel ── -->
        <div class="order-1 lg:order-2 relative select-none">

          <!-- Carousel container -->
          <div class="relative rounded-3xl overflow-hidden shadow-2xl h-[420px] md:h-[500px] lg:h-[540px]">

            <!-- Slides -->
            <transition-group name="slide-fade" tag="div" class="absolute inset-0">
              <div
                v-for="(property, i) in heroProperties"
                v-show="currentSlide === i"
                :key="property.id"
                class="absolute inset-0"
              >
                <!-- Background image -->
                <img
                  :src="property.image"
                  :alt="property.title"
                  class="w-full h-full object-cover"
                />

                <!-- Gradient overlay -->
                <div class="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>

                <!-- Top-right badge: purpose -->
                <div class="absolute top-5 right-5">
                  <span class="bg-primary-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg">
                    {{ property.purpose }}
                  </span>
                </div>

                <!-- Slide counter top-left -->
                <div class="absolute top-5 left-5 bg-black/40 backdrop-blur-sm text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                  {{ i + 1 }} / {{ heroProperties.length }}
                </div>

                <!-- Property Info Card — bottom overlay -->
                <div class="absolute bottom-0 left-0 right-0 p-5">
                  <div class="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-4">
                    <div class="flex items-start justify-between gap-3 mb-3">
                      <div class="min-w-0">
                        <p class="text-white font-bold text-base leading-snug line-clamp-1">{{ property.title }}</p>
                        <p class="text-white/70 text-xs mt-1 flex items-center gap-1">
                          <svg class="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          {{ property.location }}
                        </p>
                      </div>
                      <div class="flex-shrink-0 text-right">
                        <p class="text-white font-extrabold text-base">RWF {{ property.price }}</p>
                        <p class="text-white/60 text-xs">{{ property.pricePeriod }}</p>
                      </div>
                    </div>

                    <!-- Stats row -->
                    <div class="flex items-center gap-4">
                      <!-- Plot: land area only -->
                      <template v-if="property.propertyType === 'Plot'">
                        <div class="flex items-center gap-1.5 text-emerald-300 text-xs font-semibold">
                          <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                          </svg>
                          Land Plot
                        </div>
                        <div v-if="property.size" class="flex items-center gap-1.5 text-white/80 text-xs font-medium">
                          <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                          </svg>
                          {{ property.size }} m²
                        </div>
                      </template>
                      <!-- Other types: beds / baths / size -->
                      <template v-else>
                        <div class="flex items-center gap-1.5 text-white/80 text-xs font-medium">
                          <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                          </svg>
                          {{ property.bedrooms }} Beds
                        </div>
                        <div class="flex items-center gap-1.5 text-white/80 text-xs font-medium">
                          <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7h8m-8 5h8m-4-9v14" />
                          </svg>
                          {{ property.bathrooms }} Baths
                        </div>
                        <div v-if="property.size" class="flex items-center gap-1.5 text-white/80 text-xs font-medium">
                          <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                          </svg>
                          {{ property.size }} m²
                        </div>
                      </template>
                      <router-link
                        :to="`/property/${property.id}`"
                        class="ml-auto text-xs font-bold text-white bg-primary-500 hover:bg-primary-600 px-3 py-1.5 rounded-lg transition-colors flex-shrink-0"
                      >
                        View →
                      </router-link>
                    </div>
                  </div>
                </div>
              </div>
            </transition-group>

            <!-- Left / Right arrows -->
            <button
              @click="prevSlide"
              class="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/20 hover:bg-white/40 backdrop-blur-sm text-white rounded-full flex items-center justify-center transition-colors z-10"
            >
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <button
              @click="nextSlide"
              class="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 bg-white/20 hover:bg-white/40 backdrop-blur-sm text-white rounded-full flex items-center justify-center transition-colors z-10"
            >
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 5l7 7-7 7" />
              </svg>
            </button>

          </div>

          <!-- Dot indicators -->
          <div class="flex items-center justify-center gap-2 mt-4">
            <button
              v-for="(_, i) in heroProperties"
              :key="i"
              @click="goToSlide(i)"
              class="transition-all duration-300 rounded-full"
              :class="currentSlide === i
                ? 'w-6 h-2 bg-primary-500'
                : 'w-2 h-2 bg-neutral-300 hover:bg-neutral-400'"
            ></button>
          </div>

          <!-- Background decorations -->
          <div class="absolute -z-10 -bottom-8 -right-8 w-64 h-64 bg-primary-100 rounded-full blur-3xl opacity-60"></div>
          <div class="absolute -z-10 -top-8 -left-8 w-48 h-48 bg-accent-50 rounded-full blur-3xl opacity-60"></div>

        </div>

      </div>
    </div>
  </section>
</template>

<script lang="ts">
/* eslint-disable camelcase */
import { Options, Vue } from 'vue-class-component'
import { heroApi } from '@/api/hero'
import { propertiesApi, Property } from '@/api/properties'
import { reviewsApi } from '@/api/reviews'

interface HeroProperty {
  id: number
  title: string
  price: string
  pricePeriod: string
  location: string
  purpose: string
  propertyType: string
  bedrooms: string
  bathrooms: string
  size: string
  image: string
}

@Options({})
export default class HeroSection extends Vue {
  activeTab = 'buy'
  searchQuery = ''
  currentSlide = 0
  private autoTimer: ReturnType<typeof setInterval> | null = null

  heroProperties: HeroProperty[] = []

  reviewsLoading = true
  reviewAvatars: { initials: string; gradient: string; name: string }[] = []
  avgRating = 0
  totalReviews = 0

  private gradients = [
    'linear-gradient(135deg,#0ea5e9,#6366f1)',
    'linear-gradient(135deg,#8b5cf6,#ec4899)',
    'linear-gradient(135deg,#f59e0b,#ef4444)',
    'linear-gradient(135deg,#10b981,#0ea5e9)',
    'linear-gradient(135deg,#f97316,#eab308)',
    'linear-gradient(135deg,#6366f1,#10b981)'
  ]

  private nameInitials (name: string): string {
    const parts = name.trim().split(' ')
    return (parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '')
  }

  onSearchAction () {
    if (this.activeTab === 'buy') {
      const query: Record<string, string> = {}
      if (this.searchQuery.trim()) query.search = this.searchQuery.trim()
      this.$router.push({ name: 'properties', query })
    } else {
      // Sell: go to add-property if agent, otherwise sign up
      const user = this.$store.getters['auth/user']
      if (user?.role === 'agent') {
        this.$router.push({ name: 'agent-add-property' })
      } else {
        this.$router.push({ name: 'signup' })
      }
    }
  }

  async created () {
    // Fetch hero properties and reviews in parallel
    await Promise.allSettled([
      this.loadHeroProperties(),
      this.loadReviews()
    ])
  }

  private async loadHeroProperties () {
    try {
      let properties: Property[] = []
      const heroRes = await heroApi.getHeroProperties()
      if (heroRes.data.data.length > 0) {
        properties = heroRes.data.data
      } else {
        const fallback = await propertiesApi.list({ sort: 'popular', limit: 5 })
        properties = fallback.data.data
      }
      this.heroProperties = properties.map((p: Property) => ({
        id: p.id,
        title: p.title,
        price: p.price.toLocaleString(),
        pricePeriod: p.price_period || '',
        location: [p.sector, p.district].filter(Boolean).join(', '),
        purpose: p.purpose,
        propertyType: p.property_type,
        bedrooms: p.bedrooms,
        bathrooms: p.bathrooms,
        size: p.size ? String(p.size) : '',
        image: p.images?.[0] || ''
      }))
    } catch {
      // silently fail
    }
  }

  private async loadReviews () {
    try {
      const res = await reviewsApi.list({ limit: 50 })
      const reviews = res.data.data
      this.totalReviews = res.data.total

      if (reviews.length) {
        const sum = reviews.reduce((acc, r) => acc + r.rating, 0)
        this.avgRating = sum / reviews.length
      }

      // Take first 4 unique reviewers for the avatar stack
      this.reviewAvatars = reviews.slice(0, 4).map((r, i) => ({
        name: r.name,
        initials: this.nameInitials(r.name),
        gradient: this.gradients[i % this.gradients.length]
      }))
    } catch {
      // silently fail — badge hides gracefully
    } finally {
      this.reviewsLoading = false
    }
  }

  mounted () {
    this.startAuto()
  }

  beforeUnmount () {
    this.stopAuto()
  }

  startAuto () {
    this.autoTimer = setInterval(() => { this.nextSlide() }, 5000)
  }

  stopAuto () {
    if (this.autoTimer) clearInterval(this.autoTimer)
  }

  nextSlide () {
    if (this.heroProperties.length === 0) return
    this.currentSlide = (this.currentSlide + 1) % this.heroProperties.length
  }

  prevSlide () {
    if (this.heroProperties.length === 0) return
    this.currentSlide = (this.currentSlide - 1 + this.heroProperties.length) % this.heroProperties.length
    this.stopAuto()
    this.startAuto()
  }

  goToSlide (i: number) {
    this.currentSlide = i
    this.stopAuto()
    this.startAuto()
  }
}
</script>

<style scoped>
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: opacity 0.6s ease, transform 0.6s ease;
}
.slide-fade-enter-from {
  opacity: 0;
  transform: scale(1.04);
}
.slide-fade-leave-to {
  opacity: 0;
  transform: scale(0.96);
}
.line-clamp-1 {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
</style>
